/**
 * AuthContext — who is signed in + the `can()`-backed permission hook.
 *
 * Wired to the real API (Wave 3):
 *  - On mount: `GET /api/auth/me`. 200 → set `currentUser` from the MeResponse;
 *    401 (or any error) → `currentUser = null` so <App> shows the AuthScreen.
 *    A CSRF token is fetched in parallel so the first mutation is ready.
 *  - `login(email, password)` → `POST /api/auth/login`, refresh the CSRF token
 *    (the session changed), set `currentUser`. Errors bubble so the AuthScreen
 *    can show 401 / 429 messages.
 *  - `logout()` → `POST /api/auth/logout`, clear `currentUser`, refresh CSRF.
 *  - `register(fields, files)` → multipart `POST /api/auth/register`; returns the
 *    generic pending-approval confirmation (no session is created).
 *
 * `useCan(cap)` returns the `Decision` ('allow' | 'view' | 'deny') from the shared
 * capability matrix for the current user's RBAC role — the same `can()` the server
 * authorises with, so UI gating and server gating never drift.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { can } from '@heros/shared';
import type { Role, Capability, Decision, MeResponse } from '@heros/shared';
import {
  apiClient, ApiClientError,
  type RegisterFields, type RegisterFiles, type RegisterResponse,
} from '../lib/apiClient';

export interface AuthUser {
  id: string;
  name: string;
  initials: string;
  /** Free-text display role from the prototype. */
  role: string;
  email: string;
  /** Canonical RBAC role used by `can()`. */
  roleKey: Role;
}

interface AuthValue {
  currentUser: AuthUser | null;
  loading: boolean;
  /**
   * Sign in with credentials. Args are optional only so the App shell's
   * zero-arg "show login" affordance type-checks; AuthScreen always passes both
   * and a call with missing credentials is a no-op. `remember` (default true)
   * controls session lifetime — true → 30-day cookie, false → 12h session cookie.
   */
  login: (email?: string, password?: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  register: (fields: RegisterFields, files?: RegisterFiles) => Promise<RegisterResponse>;
}

const Ctx = createContext<AuthValue | null>(null);

/** Map a server MeResponse into the client AuthUser shape. */
function toAuthUser(me: MeResponse): AuthUser {
  const u = me.user;
  return {
    id: u.id,
    name: u.name,
    initials: u.initials,
    role: u.role,
    email: u.email,
    roleKey: (u.roleKey ?? me.roleKey) as Role,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: resolve the session and prime the CSRF token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Prime CSRF in parallel; ignore failure (mutations will refresh on demand).
      void apiClient.refreshCsrf().catch(() => undefined);
      try {
        const me = await apiClient.auth.me();
        if (!cancelled) setCurrentUser(toAuthUser(me));
      } catch (err) {
        // 401 (or anything else) → unauthenticated.
        if (!cancelled) setCurrentUser(null);
        if (!(err instanceof ApiClientError && err.isAuth)) {
          // Network / unexpected errors: stay logged out but don't crash.
          // eslint-disable-next-line no-console
          if (!(err instanceof ApiClientError)) console.warn('auth.me failed', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email?: string, password?: string, remember?: boolean) => {
    if (!email || !password) return; // zero-arg shell affordance: no-op.
    // `remember` omitted (undefined) → server default (persistent 30-day cookie),
    // preserving the pre-existing behaviour for two-arg callers.
    const me = await apiClient.auth.login({ email, password, remember });
    // Session changed — get a fresh CSRF token bound to the new session.
    await apiClient.refreshCsrf().catch(() => undefined);
    setCurrentUser(toAuthUser(me));
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.auth.logout();
    } finally {
      setCurrentUser(null);
      await apiClient.refreshCsrf().catch(() => undefined);
    }
  }, []);

  const register = useCallback(
    async (fields: RegisterFields, files?: RegisterFiles): Promise<RegisterResponse> => {
      return apiClient.auth.register(fields, files ?? {});
    },
    [],
  );

  const value: AuthValue = useMemo(
    () => ({ currentUser, loading, login, logout, register }),
    [currentUser, loading, login, logout, register],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}

/** Permission hook: `can(currentUser.roleKey, cap)`. Denies when signed out. */
export function useCan(cap: Capability): Decision {
  const { currentUser } = useAuth();
  if (!currentUser) return 'deny';
  return can(currentUser.roleKey, cap);
}
